import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  GraduationCap, Search, Sparkles, Bot, ArrowLeft, BookOpen, ChevronRight, Loader2,
} from "lucide-react";
import CourseCard from "@/components/learning/CourseCard";
import HermesChat from "@/components/HermesChat";


import TopNav from "@/components/TopNav";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORIES = ["Technical", "Leadership", "Soft Skills", "Compliance", "Safety", "Industry", "Languages"];

const FALLBACK_COURSES = [
  { id: "fc_1", title: "Public Service Administration - Level 1", description: "Foundational course for SA government administrators covering PFMA, DPSA regulations, and Batho Pele principles.", category: "Compliance", duration: "6 weeks", level: "Beginner", image_url: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=400&q=80", instructor: "Adv. Thandi Mokoena", rating: 4.5 },
  { id: "fc_2", title: "Strategic Leadership in Government", description: "Executive leadership program for senior SA public servants focusing on governance, ethics, and transformational leadership.", category: "Leadership", duration: "12 weeks", level: "Advanced", image_url: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&q=80", instructor: "Dr. Nkosana Moyo", rating: 4.8 },
  { id: "fc_3", title: "Cybersecurity Awareness for Public Sector", description: "Essential cybersecurity practices for SA government employees, covering phishing, data protection, and POPIA compliance.", category: "Technical", duration: "4 weeks", level: "Beginner", image_url: "https://images.unsplash.com/photo-1555949963-aa79dcee981c?w=400&q=80", instructor: "David Chen", rating: 4.3 },
  { id: "fc_4", title: "French Language A1 - SADC Edition", description: "Introductory French for SADC diplomats and international relations officers. Focus on diplomatic vocabulary.", category: "Languages", duration: "10 weeks", level: "Beginner", image_url: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=400&q=80", instructor: "Mme. Sophie Laurent", rating: 4.6 },
  { id: "fc_5", title: "Supply Chain Management: PFMA Compliance", description: "SCM best practices within South Africa's PFMA framework, including competitive bidding, contract management, and audit readiness.", category: "Compliance", duration: "8 weeks", level: "Intermediate", image_url: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=400&q=80", instructor: "Johan Botha", rating: 4.2 },
  { id: "fc_6", title: "Emotional Intelligence & Conflict Resolution", description: "Develop self-awareness and conflict resolution skills for the modern SA workplace. Includes practical case studies.", category: "Soft Skills", duration: "5 weeks", level: "All Levels", image_url: "https://images.unsplash.com/photo-1573496799652-408c2ac9fe98?w=400&q=80", instructor: "Dr. Penny Mthembu", rating: 4.4 },
  { id: "fc_7", title: "AI for Government: Practical Applications", description: "Understanding AI tools and their application in SA public service. Covers responsible AI, ethics, and operational efficiency.", category: "Technical", duration: "8 weeks", level: "Intermediate", image_url: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=400&q=80", instructor: "Nomsa Ndlovu", rating: 4.7 },
];

export default function LearningHub() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchCourses(), fetchEnrollments()]).finally(() => setLoading(false));
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await axios.get(`${API}/courses`, {
        headers: getHeaders(), withCredentials: true,
      });
      const data = Array.isArray(res.data) ? res.data : res.data.courses || [];
      setCourses(data.length > 0 ? data : FALLBACK_COURSES);
    } catch { setCourses(FALLBACK_COURSES); }
  };

  const fetchEnrollments = async () => {
    try {
      const res = await axios.get(`${API}/enrollments`, {
        headers: getHeaders(), withCredentials: true,
      });
      setEnrollments(Array.isArray(res.data) ? res.data : res.data.enrollments || []);
    } catch {}
  };

  const handleEnroll = async (courseId) => {
    try {
      await axios.post(`${API}/courses/${courseId}/enroll`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Enrolled successfully");
      fetchEnrollments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Enrollment failed");
    }
  };

  const enrollmentMap = {};
  enrollments.forEach((e) => { enrollmentMap[e.course_id] = e; });

  const filteredCourses = courses.filter((c) => {
    if (selectedCategory && c.category !== selectedCategory) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      return c.title?.toLowerCase().includes(q) || c.description?.toLowerCase().includes(q);
    }
    return true;
  });

  const enrolledCourses = filteredCourses.filter((c) => enrollmentMap[c.id]);
  const availableCourses = filteredCourses.filter((c) => !enrollmentMap[c.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-app flex items-center justify-center">
      <TopNav />
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-app">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-white/80 hover:text-white text-xs mb-4">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center backdrop-blur-sm">
                  <GraduationCap className="w-5 h-5" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight">Learning Hub</h1>
                  <p className="text-sm text-amber-100">Expand your skills with expert-led courses</p>
                </div>
              </div>
            </div>

            <div className="hidden sm:flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-300" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="w-64 h-9 text-sm rounded-lg bg-white/15 border-white/20 text-white placeholder:text-amber-200/70 pl-9 focus:bg-white/25"
                />
              </div>
            </div>
          </div>

          {/* Category Pills */}
          <div className="flex items-center gap-2 mt-5 flex-wrap">
            <button
              onClick={() => setSelectedCategory("")}
              className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                !selectedCategory ? "bg-white text-amber-700 font-medium" : "bg-white/15 text-white hover:bg-white/25"
              }`}
            >
              All
            </button>
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat === selectedCategory ? "" : cat)}
                className={`px-3 py-1.5 text-xs rounded-full transition-colors ${
                  selectedCategory === cat ? "bg-white text-amber-700 font-medium" : "bg-white/15 text-white hover:bg-white/25"
                }`}
              >
                {cat}
              </button>
            ))}
            {/* AI Generate */}
            <Dialog>
              <DialogTrigger asChild>
                <button className="px-3 py-1.5 text-xs rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:from-purple-600 hover:to-pink-600 transition-colors flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> Generate with AI
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl h-[600px] rounded-none">
                <DialogHeader>
                  <DialogTitle className="text-sm flex items-center gap-2">
                    <Bot className="w-4 h-4 text-purple-500" /> AI Course Generator
                  </DialogTitle>
                </DialogHeader>
                <HermesChat
                  layout="dialog"
                  contextHint="[Learning Hub - Course Generation]"
                  suggestedQuestions={[
                    "Generate a French A1 course",
                    "Create a Spanish B1 course with 12 weeks",
                    "Generate vocabulary list for business English",
                    "Create a quiz about Python programming",
                  ]}
                />
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-8">
        {/* Continue Learning */}
        {enrolledCourses.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-slate-800 flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-amber-500" /> Continue Learning
              </h2>
              {enrolledCourses.length > 3 && (
                <button
                  onClick={() => setSelectedCategory("")}
                  className="text-[11px] text-amber-600 hover:text-amber-700 font-medium flex items-center gap-0.5"
                >
                  View all <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {enrolledCourses.slice(0, 4).map((course) => (
                <CourseCard
                  key={course.id}
                  course={course}
                  enrolled
                  progress={enrollmentMap[course.id]?.progress || 0}
                />
              ))}
            </div>
          </section>
        )}

        {/* All Courses */}
        <section>
          <h2 className="text-sm font-bold text-slate-800 mb-4 flex items-center gap-2">
            All Courses {filteredCourses.length > 0 && (
              <span className="text-[11px] font-normal text-slate-400">({filteredCourses.length})</span>
            )}
          </h2>

          {filteredCourses.length === 0 ? (
            <div className="text-center py-12 text-slate-400 bg-white border border-slate-200">
              <GraduationCap className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No courses found</p>
              <p className="text-[11px] mt-1">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {availableCourses.map((course) => (
                <div key={course.id} className="relative">
                  <CourseCard
                    course={course}
                    enrolled={!!enrollmentMap[course.id]}
                    progress={enrollmentMap[course.id]?.progress || 0}
                  />
                  {!enrollmentMap[course.id] && (
                    <div className="px-3 pb-3 bg-white border border-t-0 border-slate-200">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEnroll(course.id); }}
                        className="w-full h-8 text-xs font-medium rounded-none bg-amber-500 hover:bg-amber-600 text-white transition-colors"
                      >
                        Enroll Now
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
